const INIT_TIMEOUT = 5000;
const CONNECTION_ACK = JSON.stringify({ type: "connection_ack" });

// Fired on window with the operation name as detail once the server has registered it
export const SUBSCRIPTION_CONFIRMED_EVENT = "subscription-confirmed";

interface IMessage {
  id?: string;
  type?: string;
  payload?: { data?: { status?: string; operation?: string } };
}

interface IHeldStart {
  id?: string;
  data: string;
}

const parse = (data: unknown): IMessage => {
  try {
    return JSON.parse(String(data));
  } catch {
    return {};
  }
};

/*
  The legacy WS server rejects a start that arrives before connection_init finishes and
  answers init with a { status: "success" } data frame instead of connection_ack. Hold
  starts until init is done, and replay that frame as connection_ack so
  subscriptions-transport-ws resets its reconnect state and re-subscribes on every reconnect.
*/
export const createInitAwareWebSocket = () =>
  class InitAwareWebSocket extends WebSocket {
    #heldStarts: IHeldStart[] | null = [];

    constructor(url: string | URL, protocols?: string | string[]) {
      super(url, protocols);
      let releaseTimer: ReturnType<typeof setTimeout> | undefined;

      this.addEventListener("open", () => {
        releaseTimer = setTimeout(() => this.#release(), INIT_TIMEOUT);
      });
      this.addEventListener("close", () => clearTimeout(releaseTimer));
      this.addEventListener("message", (event) => {
        const message = parse(event.data);
        const status = message.payload?.data?.status;
        if (status === "subscribed") {
          window.dispatchEvent(
            new CustomEvent(SUBSCRIPTION_CONFIRMED_EVENT, {
              detail: message.payload?.data?.operation,
            }),
          );
          return;
        }

        if (!this.#heldStarts) return;
        const isAck = message.type === "connection_ack";
        const isInitSuccess = status === "success";
        if (!isAck && !isInitSuccess) return;

        clearTimeout(releaseTimer);
        this.#release();
        if (isInitSuccess) {
          this.dispatchEvent(
            new MessageEvent("message", { data: CONNECTION_ACK }),
          );
        }
      });
    }

    send(data: string) {
      if (!this.#heldStarts) return super.send(data);

      const { id, type } = parse(data);
      if (type === "start") {
        this.#heldStarts.push({ id, data });
        return;
      }
      // A subscription stopped before init never reached the server
      if (
        type === "stop" &&
        this.#heldStarts.some((start) => start.id === id)
      ) {
        this.#heldStarts = this.#heldStarts.filter((start) => start.id !== id);
        return;
      }
      super.send(data);
    }

    #release() {
      const held = this.#heldStarts ?? [];
      this.#heldStarts = null;
      held.forEach(({ data }) => super.send(data));
    }
  };
